import { useSyncExternalStore } from "react";
import type { WritingGoals } from "@quill/shared";
import { NetworkError, api } from "./api";

/**
 * Writing goals and words written. The server logs net words written per 15 minutes as it
 * stores documents (every device, a few seconds behind the typing); this device adds the
 * words it wrote since its last fetch, so the numbers move as you type and offline.
 *
 * The session is this device's writing since the app was opened, and ends once the app
 * has been closed or in the background for 30 minutes.
 */

const MINUTE = 60_000;
const DAY = 24 * 60 * MINUTE;
const SESSION_GAP_MS = 30 * MINUTE;
/** The server stores 2 s after typing pauses; ask a little after that. */
const FETCH_AFTER_WRITING_MS = 4_000;
/** Picks up other devices' writing and keeps the windows rolling. */
const REFRESH_MS = 5 * MINUTE;
/** Pace for daily goals runs over the writing day, not the whole 24 hours. */
const DAY_START_HOUR = 6;
const DAY_END_HOUR = 22;

export type Period = "session" | "day" | "week" | "month";
export const PERIOD_DAYS: Record<Exclude<Period, "session">, number> = { day: 1, week: 7, month: 30 };

export type GoalStatus = "behind" | "on-track" | "ahead" | "met";

export interface GoalProgress {
  kind: "daily" | "weekly";
  target: number;
  /** Words written today / this week. */
  done: number;
  status: GoalStatus;
  /** Identifies the day or week, so a goal is celebrated once per period. */
  periodKey: string;
}

export interface Progress {
  goals: WritingGoals;
  sessionStartedAt: number;
  /** Words written in each period (session, last 1, 7 and 30 days). */
  written: Record<Period, number>;
  daily: GoalProgress | null;
  weekly: GoalProgress | null;
}

interface Saved {
  goals: WritingGoals;
  /** Goals changed on this device and not yet saved on the server. */
  goalsPending: boolean;
  buckets: { at: number; words: number }[];
  /** Words written on this device since `buckets` was fetched. */
  unsent: number;
  session: { startedAt: number; words: number; seenAt: number };
  /** The day / week each goal was last celebrated for on this device. */
  celebrated: { daily?: string; weekly?: string };
}

const NO_GOALS: WritingGoals = { daily: null, weekly: null };

let userId: string | null = null;
let saved: Saved = blank();
let snapshot: Progress = compute(saved, Date.now());
const listeners = new Set<() => void>();

function blank(): Saved {
  const now = Date.now();
  return { goals: NO_GOALS, goalsPending: false, buckets: [], unsent: 0, session: { startedAt: now, words: 0, seenAt: now }, celebrated: {} };
}

const storageKey = () => `quill.progress.${userId}`;

function load(): Saved {
  try {
    const raw = localStorage.getItem(storageKey());
    return raw ? { ...blank(), ...(JSON.parse(raw) as Partial<Saved>) } : blank();
  } catch {
    return blank();
  }
}

function update(change: Partial<Saved>) {
  saved = { ...saved, ...change };
  try {
    localStorage.setItem(storageKey(), JSON.stringify(saved));
  } catch {
    // Storage full or blocked: the numbers still work until the app is closed.
  }
  notify();
}

function notify() {
  snapshot = compute(saved, Date.now());
  listeners.forEach((l) => l());
}

// ---- time ------------------------------------------------------------------------

function startOfDay(t: number): number {
  const d = new Date(t);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** First day of the week for the user's locale (1 = Monday … 7 = Sunday); Monday if unknown. */
function firstDayOfWeek(): number {
  try {
    const locale = new Intl.Locale(navigator.language) as Intl.Locale & {
      getWeekInfo?(): { firstDay: number };
      weekInfo?: { firstDay: number };
    };
    return (locale.getWeekInfo?.() ?? locale.weekInfo)?.firstDay ?? 1;
  } catch {
    return 1;
  }
}
const weekStartsOn = firstDayOfWeek() % 7; // as Date.getDay(): 0 = Sunday

function startOfWeek(t: number): number {
  const day = new Date(t);
  const back = (day.getDay() - weekStartsOn + 7) % 7;
  return new Date(day.getFullYear(), day.getMonth(), day.getDate() - back).getTime();
}

/** How much of today's writing day has passed, 0 to 1. */
function dayElapsed(t: number): number {
  const hours = (t - startOfDay(t)) / (60 * MINUTE);
  return Math.min(1, Math.max(0, (hours - DAY_START_HOUR) / (DAY_END_HOUR - DAY_START_HOUR)));
}

const dateKey = (t: number) => {
  const d = new Date(t);
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
};

// ---- progress --------------------------------------------------------------------

function statusOf(done: number, target: number, expected: number): GoalStatus {
  if (done >= target) return "met";
  // Nothing expected yet (before the writing day starts, or at the start of the week).
  if (expected < 1) return done > 0 ? "ahead" : "on-track";
  const pace = done / expected;
  return pace >= 1.1 ? "ahead" : pace >= 0.9 ? "on-track" : "behind";
}

function compute(s: Saved, now: number): Progress {
  const since = (from: number) => s.buckets.reduce((n, b) => (b.at >= from ? n + b.words : n), 0) + s.unsent;
  const today = startOfDay(now);
  const week = startOfWeek(now);
  const sessionLive = now - s.session.seenAt <= SESSION_GAP_MS;

  let daily: GoalProgress | null = null;
  if (s.goals.daily) {
    const done = since(today);
    daily = {
      kind: "daily",
      target: s.goals.daily,
      done,
      status: statusOf(done, s.goals.daily, s.goals.daily * dayElapsed(now)),
      periodKey: dateKey(today),
    };
  }
  let weekly: GoalProgress | null = null;
  if (s.goals.weekly) {
    const done = since(week);
    const days = Math.round((today - week) / DAY) + dayElapsed(now);
    weekly = {
      kind: "weekly",
      target: s.goals.weekly,
      done,
      status: statusOf(done, s.goals.weekly, (s.goals.weekly * days) / 7),
      periodKey: dateKey(week),
    };
  }
  return {
    goals: s.goals,
    sessionStartedAt: s.session.startedAt,
    written: {
      session: sessionLive ? s.session.words : 0,
      day: since(now - DAY),
      week: since(now - 7 * DAY),
      month: since(now - 30 * DAY),
    },
    daily,
    weekly,
  };
}

// ---- server ----------------------------------------------------------------------

let fetching = false;
let fetchTimer: ReturnType<typeof setTimeout> | undefined;

async function refresh() {
  if (!userId || fetching || !navigator.onLine) return;
  fetching = true;
  const user = userId;
  const unsentAtStart = saved.unsent;
  try {
    if (saved.goalsPending) {
      const goals = saved.goals;
      await api.setGoals(goals);
      if (user !== userId) return;
      // Changed again while saving: that newer change still needs to go.
      if (saved.goals === goals) update({ goalsPending: false });
    } else {
      const goals = await api.goals();
      if (user !== userId) return;
      if (!saved.goalsPending) update({ goals });
    }
    const { buckets } = await api.wordsWritten(Date.now() - 31 * DAY);
    if (user !== userId) return;
    // Words written while this was in flight may not be in the answer yet.
    update({ buckets, unsent: saved.unsent - unsentAtStart });
  } catch (err) {
    if (!(err instanceof NetworkError)) console.warn("progress refresh failed", err);
  } finally {
    fetching = false;
    if (saved.goalsPending && user === userId) scheduleRefresh(FETCH_AFTER_WRITING_MS);
  }
}

function scheduleRefresh(ms: number) {
  clearTimeout(fetchTimer);
  fetchTimer = setTimeout(() => void refresh(), ms);
}

/** Starts tracking for the signed-in user; returns the cleanup. */
export function openProgress(id: string): () => void {
  userId = id;
  saved = load();
  markSeen();
  void refresh();

  const onVisibility = () => {
    markSeen();
    if (document.visibilityState === "visible") void refresh();
  };
  const onOnline = () => void refresh();
  // While open: keep the session alive, roll the windows forward, catch other devices' writing.
  const tick = setInterval(() => {
    if (document.visibilityState !== "visible") return;
    markSeen();
    notify();
  }, MINUTE);
  const poll = setInterval(() => document.visibilityState === "visible" && void refresh(), REFRESH_MS);
  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("pagehide", markSeen);
  window.addEventListener("online", onOnline);
  return () => {
    markSeen();
    clearInterval(tick);
    clearInterval(poll);
    clearTimeout(fetchTimer);
    document.removeEventListener("visibilitychange", onVisibility);
    window.removeEventListener("pagehide", markSeen);
    window.removeEventListener("online", onOnline);
    userId = null;
    saved = blank();
    notify();
  };
}

/** Notes that the app is in use now, starting a new session after a long enough gap. */
function markSeen() {
  if (!userId) return;
  const now = Date.now();
  const { session } = saved;
  update({ session: now - session.seenAt > SESSION_GAP_MS ? { startedAt: now, words: 0, seenAt: now } : { ...session, seenAt: now } });
}

/** Called by the editor with the net words typed on this device (negative for deletions). */
export function recordWritten(words: number) {
  if (!userId || !words) return;
  const now = Date.now();
  const session = now - saved.session.seenAt > SESSION_GAP_MS ? { startedAt: now, words: 0, seenAt: now } : saved.session;
  update({ unsent: saved.unsent + words, session: { ...session, words: session.words + words, seenAt: now } });
  scheduleRefresh(FETCH_AFTER_WRITING_MS);
}

export function setGoals(goals: WritingGoals) {
  if (!userId) return;
  update({ goals, goalsPending: true });
  void refresh();
}

/** True the first time a goal is met in its day or week (on this device); false after that. */
export function claimCelebration(goal: GoalProgress): boolean {
  if (!userId || goal.status !== "met" || saved.celebrated[goal.kind] === goal.periodKey) return false;
  update({ celebrated: { ...saved.celebrated, [goal.kind]: goal.periodKey } });
  return true;
}

export function useProgress(): Progress {
  return useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    () => snapshot,
  );
}
